import React, { useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { javascript, javascriptLanguage } from "@codemirror/lang-javascript";
import { keymap } from "@codemirror/view";
import { Prec } from "@codemirror/state";
import { linter, lintGutter } from "@codemirror/lint";
import { parse } from "acorn";

// Syntax linter. The node code is the BODY of an async function, so we allow
// top-level `return`/`await`. Underlines syntax errors as you type.
const jsSyntax = linter(view => {
  const code = view.state.doc.toString();
  if (!code.trim()) return [];
  try {
    parse(code, {
      ecmaVersion: "latest",
      sourceType: "script",
      allowReturnOutsideFunction: true,
      allowAwaitOutsideFunction: true,
    });
    return [];
  } catch (e) {
    const len = code.length;
    const pos = typeof e.pos === "number" ? Math.max(0, Math.min(e.pos, len)) : 0;
    const from = pos >= len && pos > 0 ? pos - 1 : pos;
    return [{ from, to: Math.min(from + 1, len), severity: "error", message: e.message }];
  }
});

// autocompletar extra (opcional): `completions` = [{ label: "studio.segredo", detail, info, apply }].
// Soma-se ao autocompletar normal do JavaScript; casa pelo caminho digitado
// (ex.: "studio.se" → studio.segredo).
const extraCompletions = items => ctx => {
  const word = ctx.matchBefore(/[\w$.]+/);
  if (!word || (word.from === word.to && !ctx.explicit)) return null;
  return { from: word.from, options: items, validFor: /^[\w$.]*$/ };
};

// CodeMirror 6 editor with JS highlight, line numbers, brackets and syntax lint.
// Exposes an imperative `insertAtCursor(text)` via ref so callers (e.g. the
// variable picker) can drop a `steps.*` reference at the caret.
const CodeEditor = forwardRef(
  ({ value, onChange, readOnly, height = "260px", completions, onSave }, ref) => {
    // Ctrl/Cmd+S → onSave (via ref: a extensão é criada uma vez só)
    const saveRef = useRef(onSave);
    saveRef.current = onSave;
    const extensions = useMemo(() => {
      const ext = [javascript(), jsSyntax, lintGutter()];
      if (completions && completions.length) ext.push(javascriptLanguage.data.of({ autocomplete: extraCompletions(completions) }));
      ext.push(Prec.highest(keymap.of([{ key: "Mod-s", preventDefault: true, run: () => { if (saveRef.current) saveRef.current(); return !!saveRef.current; } }])));
      return ext;
    }, [completions]);
    const cmRef = useRef(null);

    useImperativeHandle(ref, () => ({
      insertAtCursor: text => {
        const view = cmRef.current && cmRef.current.view;
        if (!view) return;
        const sel = view.state.selection.main;
        view.dispatch({
          changes: { from: sel.from, to: sel.to, insert: text },
          selection: { anchor: sel.from + text.length },
        });
        view.focus();
      },
    }));

    return (
      <CodeMirror
        ref={cmRef}
        value={value || ""}
        height={height}
        editable={!readOnly}
        readOnly={readOnly}
        extensions={extensions}
        onChange={v => onChange && onChange(v)}
        basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: !readOnly }}
      />
    );
  }
);

export default CodeEditor;
